import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { X, CircleNotch, CaretLeft, CaretRight, ArrowCounterClockwise, Stamp, Eye, PencilSimple, ArrowsOutCardinal, ArrowsOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import api from "../lib/api";

export const ManualQrEditor = ({ machine, onClose, onSaved }) => {
  const { t } = useLang();
  const q = t.qredit;
  const availableDocs = [machine.manual_file_path && "manual", machine.declaration_file_path && "declaration"].filter(Boolean);
  const [docType, setDocType] = useState(availableDocs[0] || "manual");
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(0);
  const [imgUrl, setImgUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [stamped, setStamped] = useState(false);
  const [box, setBox] = useState({ x: 0.68, y: 0.05, size: 0.22 });
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(() => !localStorage.getItem("cqr_qredit_guide"));
  const dismissGuide = () => { localStorage.setItem("cqr_qredit_guide", "1"); setShowGuide(false); };
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const qrTarget = `${process.env.REACT_APP_BACKEND_URL}/m/${machine.id}`;

  useEffect(() => {
    setPages(0);
    setPage(0);
    setPreviewUrl(null);
    setBox({ x: 0.68, y: 0.05, size: 0.22 });
    api.get(`/machines/${machine.id}/docedit/${docType}/pages`)
      .then(({ data }) => {
        setPages(data.pages);
        setStamped(data.stamped);
        if (data.stamp) {
          setBox({ x: data.stamp.x, y: data.stamp.y, size: data.stamp.size });
          setPage(Math.min(data.stamp.page, data.pages - 1));
        }
      })
      .catch(() => { toast.error(q.loadError); onClose(); });
  }, [machine.id, docType]);

  useEffect(() => {
    if (!pages) return;
    let url;
    setImgUrl(null);
    setPreviewUrl(null);
    api.get(`/machines/${machine.id}/docedit/${docType}/page/${page}`, { responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(res.data); setImgUrl(url); })
      .catch(() => toast.error(q.loadError));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [page, pages, machine.id, docType]);

  const clamp = (b) => {
    const size = Math.min(Math.max(b.size, 0.06), 0.6);
    const rect = containerRef.current?.getBoundingClientRect();
    const ratio = rect ? rect.width / rect.height : 0.7;
    const hNorm = size * 1.14 * ratio;
    return {
      size,
      x: Math.min(Math.max(b.x, 0), 1 - size),
      y: Math.min(Math.max(b.y, 0), Math.max(0, 1 - hNorm)),
    };
  };

  const onPointerDown = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, box: { ...box }, rect };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.rect.width;
    const dy = (e.clientY - d.startY) / d.rect.height;
    if (d.mode === "move") {
      setBox(clamp({ ...d.box, x: d.box.x + dx, y: d.box.y + dy }));
    } else {
      setBox(clamp({ ...d.box, size: d.box.size + dx }));
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/machines/${machine.id}/docedit/${docType}/stamp`, { page, ...box });
      toast.success(q.saved);
      setStamped(true);
      setPreviewUrl(null);
      onSaved?.();
    } catch (e) {
      toast.error(q.error);
    } finally {
      setSaving(false);
    }
  };

  const restore = async () => {
    try {
      await api.post(`/machines/${machine.id}/docedit/${docType}/restore`);
      toast.success(q.restored);
      setStamped(false);
      onSaved?.();
    } catch (e) {
      toast.error(q.error);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(`/machines/${machine.id}/docedit/${docType}/preview`, { page, ...box }, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch (e) {
      toast.error(q.error);
    } finally {
      setPreviewing(false);
    }
  };

  const rect = containerRef.current?.getBoundingClientRect();
  const boxPxW = rect ? box.size * rect.width : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0F1A]/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-md border border-slate-200 max-h-[94vh] flex flex-col" data-testid="qr-editor-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-display font-bold text-xl text-[#0F172A] flex items-center gap-2"><Stamp size={20} className="text-emerald-600" /> {q.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{machine.name} · {q.subtitle}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]" data-testid="qr-editor-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {availableDocs.length > 1 && (
            <div className="mb-3 inline-flex items-center rounded-full bg-slate-100 p-1" data-testid="qr-editor-doc-tabs">
              {availableDocs.map((dt) => (
                <button key={dt} onClick={() => setDocType(dt)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${docType === dt ? "bg-[#0A0F1A] text-white" : "text-slate-600"}`}
                  data-testid={`qr-editor-tab-${dt}`}>
                  {dt === "manual" ? t.dash.manualShort : t.dash.declShort}
                </button>
              ))}
            </div>
          )}
          {stamped && (
            <div className="mb-3 rounded-md bg-slate-50 border border-slate-200 px-4 py-2.5 text-xs text-slate-600" data-testid="qr-editor-stamped-note">{q.stampedNote}</div>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2" data-testid="qr-editor-pagination">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="rounded-full border border-slate-300 p-2 text-slate-600 disabled:opacity-40 hover:border-emerald-500 transition-colors" data-testid="qr-editor-prev-page"><CaretLeft size={14} /></button>
              <span className="font-mono-tech text-xs text-slate-600" data-testid="qr-editor-page-indicator">{q.page} {page + 1} {q.of} {pages || "…"}</span>
              <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
                className="rounded-full border border-slate-300 p-2 text-slate-600 disabled:opacity-40 hover:border-emerald-500 transition-colors" data-testid="qr-editor-next-page"><CaretRight size={14} /></button>
            </div>
            {stamped && (
              <button onClick={restore} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors" data-testid="qr-editor-restore">
                <ArrowCounterClockwise size={14} /> {q.restore}
              </button>
            )}
          </div>

          <div ref={containerRef} className="relative select-none border border-slate-300 rounded-sm overflow-hidden bg-slate-100 min-h-[300px]" data-testid="qr-editor-canvas">
            {previewUrl ? (
              <img src={previewUrl} alt="anteprima" className="w-full h-auto block" draggable={false} data-testid="qr-editor-preview-img" />
            ) : imgUrl ? (
              <img src={imgUrl} alt={`page ${page + 1}`} className="w-full h-auto block pointer-events-none" draggable={false} />
            ) : (
              <div className="flex justify-center items-center py-32"><CircleNotch size={28} className="text-emerald-500 animate-spin" /></div>
            )}
            {showGuide && imgUrl && !previewUrl && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0A0F1A]/70 backdrop-blur-[2px] p-6" data-testid="qr-editor-guide">
                <div className="max-w-sm w-full bg-white rounded-md border border-slate-200 p-6">
                  <h4 className="font-display font-bold text-lg text-[#0F172A]">{q.guideTitle}</h4>
                  <ol className="mt-4 space-y-3">
                    {q.guideSteps.map((step, i) => {
                      const Icon = [ArrowsOutCardinal, ArrowsOut, Eye, Stamp][i];
                      return (
                        <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                          <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Icon size={14} weight="bold" /></span>
                          <span className="pt-1">{step}</span>
                        </li>
                      );
                    })}
                  </ol>
                  <button onClick={dismissGuide}
                    className="mt-5 w-full rounded-full bg-emerald-500 py-2.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors"
                    data-testid="qr-editor-guide-ok">{q.guideOk}</button>
                </div>
              </div>
            )}
            {!previewUrl && imgUrl && (
              <div
                className="absolute bg-white border-2 border-emerald-500 shadow-lg cursor-move flex flex-col items-center justify-start"
                style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.size * 100}%` }}
                onPointerDown={(e) => onPointerDown(e, "move")}
                data-testid="qr-editor-box"
              >
                <div style={{ padding: `${boxPxW * 0.06}px` }} className="w-full">
                  <QRCodeSVG value={qrTarget} level="M" style={{ width: "100%", height: "auto" }} />
                </div>
                <span className="w-full text-center font-mono-tech text-slate-600 leading-none pb-1" style={{ fontSize: `${Math.max(boxPxW * 0.07, 6)}px` }}>
                  S/N {machine.serial_number}
                </span>
                <span
                  onPointerDown={(e) => onPointerDown(e, "resize")}
                  className="absolute -bottom-2 -right-2 h-4 w-4 rounded-full bg-emerald-500 border-2 border-white cursor-nwse-resize"
                  data-testid="qr-editor-resize-handle"
                />
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">{previewUrl ? q.previewNote : q.hint}</p>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-3">
          <button onClick={onClose} className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="qr-editor-cancel">{t.plate.close}</button>
          {previewUrl ? (
            <button onClick={() => setPreviewUrl(null)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-500 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors" data-testid="qr-editor-back-edit">
              <PencilSimple size={15} /> {q.backEdit}
            </button>
          ) : (
            <button onClick={preview} disabled={previewing || !imgUrl}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-500 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-60" data-testid="qr-editor-preview">
              {previewing ? <CircleNotch size={15} className="animate-spin" /> : <Eye size={15} />} {q.previewBtn}
            </button>
          )}
          <button onClick={save} disabled={saving || !imgUrl}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-500 py-3 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="qr-editor-save">
            {saving ? <><CircleNotch size={15} className="animate-spin" /> {q.saving}</> : <><Stamp size={15} /> {q.save}</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
